import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as SearchMagnifyIcon } from '../../assets/svg/SearchMagnifyIcon.svg';
import Loading from '../Loading';
import { getAllStruggles } from '../../server/server';
import { notify } from '../../utils/util';

function StruggleRecommendationsHome() {
	const navigate = useNavigate();
	const [struggles, setStruggles] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const timeout = useRef<any>(null);
	const isFirstMount = useRef(true);

	useEffect(() => {
		if (isFirstMount.current) {
			isFirstMount.current = false;
			loadStruggles();
			return;
		}
	}, []);

	const loadStruggles = async () => {
		setLoading(true);
		const resp = await getAllStruggles();

		if (resp.status === 200) {
			const data = resp.data || [];
			setStruggles(data);
			setLoading(false);
		} else {
			setLoading(false);
			notify('Failed to load struggles');
		}
	};

	const handleSearchChange = (e: any) => {
		e.preventDefault();
		setSearch(e.target.value);
	};

	const openStruggle = (struggle: any) => {
		navigate(`/struggle-recommendations/${struggle.id}`);
	};

	const filteredStruggles = struggles.filter((struggle) => {
		if (!search) return true;
		const searchLower = search.toLowerCase();
		return struggle.name?.toLowerCase().includes(searchLower) || struggle.code?.toLowerCase().includes(searchLower) || struggle.description?.toLowerCase().includes(searchLower);
	});

	return (
		<div id='scroll-inf-struggles' className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className='w-full max-w-[1200px] px-[10px]'>
				<div className='w-full min-h-[calc(100vh-80px)] bg-white flex flex-col px-[40px] py-[30px] rounded-[10px]'>
					<div className='mb-[30px] flex flex-row items-end gap-4'>
						<p className='flex-1 text-[40px] leading-[50px] font-bold text-darkViolet'>Struggle Recommendations</p>
					</div>

					<div className='flex w-full flex-row gap-5 mb-5'>
						<div className='flex h-[50px] flex-1 flex-row items-center rounded-full border border-primaryViolet px-4'>
							<input type='text' placeholder='Search struggles' value={search} onChange={handleSearchChange} className='h-full flex-1 rounded-full outline-none text-[18px] leading-6 font-semibold text-primaryViolet placeholder:font-medium placeholder:text-mudPurple' autoComplete='off' />
							<SearchMagnifyIcon />
						</div>
					</div>

					{filteredStruggles.length === 0 && !loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<p className='text-center text-[14px] font-medium leading-5 text-mudPurple'>
								No struggles found.
								<br />
								{search ? 'Try a different search term.' : 'No struggles available.'}
							</p>
						</div>
					)}

					{!loading && (
						<div className='flex-1 overflow-auto'>
							{filteredStruggles.map((struggle: any) => (
								<StruggleCard key={struggle.id} struggle={struggle} onClick={() => openStruggle(struggle)} />
							))}
						</div>
					)}

					{loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default StruggleRecommendationsHome;

const StruggleCard = ({ struggle, onClick }: { struggle: any; onClick: () => void }) => {
	return (
		<div className='my-[5px] flex cursor-pointer flex-row gap-5 rounded-[10px] border border-[#F8F8FB] bg-[#F8F8FB] px-[40px] py-[25px] duration-300 ease-out hover:border-mudPurple' onClick={onClick}>
			<div className='flex flex-1 flex-col justify-center gap-2'>
				<div className='flex flex-row items-center gap-3'>
					<h3 className='text-[20px] font-semibold leading-7 text-primaryViolet'>{struggle.name}</h3>

					{!struggle.isActive && <span className='px-3 py-1 rounded-full text-[12px] font-bold uppercase text-gray-600 bg-gray-200'>INACTIVE</span>}
				</div>
				{struggle.description && <p className='text-[14px] font-medium leading-5 text-mudPurple mt-1'>{struggle.description}</p>}
			</div>
			<div className='flex items-center justify-center shrink-0'>
				<div className='px-4 py-2 rounded-full border border-primaryViolet bg-white text-[14px] font-semibold text-primaryViolet transition hover:bg-primaryViolet hover:text-white'>Manage Recommendations</div>
			</div>
		</div>
	);
};
